<template>
  <div class="goals-wrapper">
    <details>
      <summary @click="openAccordion" id="goal-summary">
        <span class="summary-wrapper">
          <Warning color="#ffc938" v-if="!hasExistingGoal"></Warning>
          <Check color="#00B0AA" v-else></Check>
          <span v-if="hasExistingGoal">View your sleep goals</span>
          <span v-else>Set your sleep goals</span>
        </span>

        <span class="arrow">
          <Arrow color="#fff"></Arrow>
        </span>
      </summary>

      <div class="details-content">
        <div class="goals-list" v-if="hasExistingGoal && !editSleepGoals">
          <div v-for="goalGroup in existingGoals" :key="goalGroup[0]">
            <header class="goal-group-header">{{ goalGroup[0] | unMachine }}</header>
            <ul>
              <li class="goal" v-for="goal in goalGroup[1]" :key="goal.name">{{ goal.label }}</li>
            </ul>
          </div>

          <button @click="editSleepGoals = true" class="button button--standard">Edit sleep goals</button>
        </div>

        <form @submit.prevent="submitForm" v-if="!hasExistingGoal || editSleepGoals">
          <div class="goal-section" v-for="goalSection in goalSections" :key="goalSection[0]">
            <header class="goal-group-header">{{ goalSection[0] | unMachine }}</header>

            <div class="checkbox" v-for="(goal, index) in goalSection[1]" :key="goal.name">
              <input
                type="checkbox"
                :name="goal.name"
                :id="goal.name"
                v-model="form[goalSection[0]][index].value"
              >
              <label :for="goal.name">{{ goal.label }}</label>
            </div>
          </div>

          <button class="button button--standard" type="submit">
            <span v-if="editSleepGoals">Update sleep goals</span>
            <span v-else>Set sleep goals</span>
          </button>

          <button
            class="button button--danger"
            v-if="editSleepGoals"
            @click="editSleepGoals = false"
          >Cancel</button>
        </form>
      </div>
    </details>
    <hr>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import Utility from "@/mixins/utility";

import Warning from "@/components/svg/Warning.vue";
import Arrow from "@/components/svg/Arrow.vue";
import Check from "@/components/svg/Check.vue";

import firebase from "firebase/app";
import "firebase/firestore";

export default {
  name: "Goals",

  data() {
    return {
      existingGoal: {},
      existingGoals: [],
      editSleepGoals: false,
      hasExistingGoal: false,
      form: {
        "be-mindful-of-what-you-consume": [
          {
            name: "avoid-caffeine",
            label: "Avoid caffeine and nicotine",
            value: false
          },
          {
            name: "light-snack",
            label: "Eat a light snack before bedtime",
            value: false
          },
          {
            name: "avoid-heavy-meals",
            label: "Avoid heavy meals close to bedtime",
            value: false
          }
        ],
        "bedroom-tips": [
          {
            name: "bedroom-temperature",
            label: "Keep the bedroom temperature comfortable",
            value: false
          },
          {
            name: "bedroom-dark",
            label: "Keep the bedroom dark",
            value: false
          },
          {
            name: "bed-comfortable",
            label: "Make sure the bed is comfortable. If needed, adjust the padding thickness on your mattress to your liking",
            value: false
          },
          {
            name: "adjust-pillows",
            label: "Adjust or change pillows/mattress to better support sore or painful areas",
            value: false
          },
          {
            name: "remove-pets",
            label: "Keep your bed free of pets when you sleep",
            value: false
          }
        ],
        "body-temperature-tips": [
          {
            name: "warm-bath",
            label: "Take a warm bath shortly before bedtime",
            value: false
          },
          {
            name: "avoid-exercise",
            label: "Avoid exercising within 2 to 3 hours of bedtime",
            value: false
          }
        ],
        "calming-tips": [
          {
            name: "calm-activity",
            label: "Do something that is calming before going to bed. Listen to music, read a magazine, or meditate",
            value: false
          },
          {
            name: "avoid-tv",
            label: "Avoid watching stimulating TV programs close to bedtime",
            value: false
          },
          {
            name: "worry-book",
            label: "If you find yourself worrying about something before bedtime, write down your thoughts in a \"worry book\". Set aside time the next day to think about the problem and how to solve it",
            value: false
          },
        ],
        "good-sleep-behaviors": [
          {
            name: "bed-sleepy",
            label: "Go to bed only when sleepy",
            value: false
          },
          {
            name: "out-bed",
            label: "Get out of bed if you can't sleep",
            value: false
          },
          {
            name: "watch-clock",
            label: "Try not to watch the clock",
            value: false
          },
          {
            name: "no-read-eat",
            label: "Don't read, eat, or watch TV in bed",
            value: false
          }
        ],
        "timing": [
          {
            name: "bed-same-time",
            label: "Go to bed at the same time each night",
            value: false
          },
          {
            name: "wake-same-time",
            label: "Get up at the same time each morning",
            value: false
          },
          {
            name: "avoid-naps",
            label:
              "Avoid napping if you can. If not, keep it to 30 to 60 minutes at maximum",
            value: false
          }
        ]
      }
    };
  },

  components: {
    Warning,
    Arrow,
    Check
  },

  mixins: [Utility],

  async mounted() {
    // Check for existing goal.
    await this.checkforGoal();

    if (this.hasExistingGoal) {
      // Set existing goal as our form data.
      this.form = this.existingGoal.data().data;

      // Create an array of our goals.
      const goals = Object.entries(this.form);

      // Loop through and set goals to our data array.
      for (const goalGroup of goals) {
        // Filter out any false values.
        goalGroup[1] = goalGroup[1].filter(goal => goal.value);

        // Set the goal group to our existing goals data array.
        if (goalGroup[1].length) {
          this.existingGoals.push(goalGroup);
        }
      }
    }
  },

  computed: {
    ...mapGetters(["getDB", "getUID"]),

    goalSections() {
      return Object.entries(this.form);
    }
  },

  methods: {
    ...mapMutations(["modifyLoading"]),

    async submitForm() {
      // Set loading to true.form
      this.modifyLoading(true);

      // Check for an existing document.
      await this.checkforGoal();

      // Check to see if a goal currently exists.
      if (this.hasExistingGoal) {
        // Update current goal.
        await this.updateSleepGoal(this.existingGoal.id);
      } else {
        // Create new goal document for the user.
        await this.createSleepGoal();
      }

      // Disable the loading spinner.
      this.disableLoader(1000);
    },

    // Checks to see if any existing goal is present and stores it.
    checkforGoal() {
      return this.getDB
        .collection("goals")
        .where("module", "==", "sleep")
        .where("user", "==", this.getUID)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            this.existingGoal = doc;
            this.hasExistingGoal = true;
          });
        })
        .catch(error => {
          this.hasExistingGoal = false;
        });
    },

    updateSleepGoal(id) {
      const goalData = this.existingGoal.data();

      // Add in our new data.
      goalData.data = this.form;

      // Make the update.
      return this.getDB
        .collection("goals")
        .doc(id)
        .update(goalData);
    },

    // Creates a goal entry.
    createSleepGoal() {
      return this.getDB.collection("goals").add({
        user: this.getUID,
        module: "sleep",
        data: this.form
      });
    }
  }
};
</script>

<style scoped>
header {
  font-weight: 600;
  margin-bottom: 1rem;
}
button {
  margin-top: 1rem;
}
summary {
  align-items: center;
  background-color: #002b4c;
  display: flex;
  justify-content: space-between;
}
summary svg {
  margin-right: 1rem;
}
.summary-wrapper {
  align-items: center;
  display: flex;
}
.goals-wrapper {
  margin-bottom: 2rem;
}
.details-content {
  padding: 2rem;
}
.goal-section {
  margin-bottom: 2rem;
}
.goal-group-header {
  font-weight: 600;
  text-transform: uppercase;
}
.goal {
  margin-bottom: 0.5rem;
}
</style>
